import React, { useEffect, useState } from "react";
import { List, Tag } from "antd";
import { ClockCircleOutlined } from "assets/icons";
import PageLayout from "layout/PageLayout/PageLayout";
import CustomBreadCrumb from "components/CustomBreadCrumb/CustomBreadCrumb";
import "./Crashes.css";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useDispatch, useSelector } from "react-redux";
import centralApi from "services/centralApi";
import { useLocation, useNavigate } from "react-router-dom";
import CrashDetailedView from "./CrashDetailedView/CrashDetailedView";
import CrashSessionCard from "./components/CrashSessionCard";
import CustomHighChart from "components/Charts/CustomHighChart/CustomHighChart";
import Selector from "components/Selector/Selector";
import SkeletonLoader from "components/SkeletonLoader/SkeletonLoader";
import { ApmApiMethods, ApmApiFields } from "utils/enums";
import { API_ENDPOINTS, ROUTES, apmTimeRange } from "utils/constants";
import { t } from "i18next";
import useMessage from "hooks/useMessage";
import { PlatformEnum } from "src_typescript_appice-core/dist/constants";
import { getLocalStorage } from "helperFunctions/localStorage";
import { setCrashAnalytics } from "redux/features/crash/crashAnalytics";
import { DateRangeApmSelector } from "./components/DateRangeApmSelector";
import {
  getApmInterval,
  getApmTimeRange,
} from "utils/constants/selectorOptions";
const Crashes: React.FC = () => {
  dayjs.extend(relativeTime);
  const navigate = useNavigate();
  const location = useLocation();
  const loginUser = useSelector((state: any) => state.loginUser.data);
  const { appId } = useSelector((state: any) => state.activeApp);
  const [timeRange, setTimeRange] = useState(apmTimeRange.statsPeriod.default);
  const dispatch = useDispatch();
  const savedVersion = useSelector(
    (state: any) => state.crashAnalytics?.appVersion
  );
  const [query, setQuery] = useState(
    savedVersion || getLocalStorage("appVersion") || ""
  );
  const [platformFilter, setPlatformFilter] = useState<string>(
    PlatformEnum.ALL
  );
  const getPlatformQuery = () => {
    switch (platformFilter) {
      case PlatformEnum.ANDROID:
        return "os.name:Android";
      case PlatformEnum.IOS:
        return "os.name:iOS";
      default:
        return ""; // 'all'
    }
  };

  const handleRangeChange = (value: any) => {
    if (typeof value === "string") {
      // Preset selected (like "7d" or "1h")
      setTimeRange(value);
    } else if (value?.start && value?.end) {
      setTimeRange({
        start: `${value.start}T00:00:00.000`,
        end: `${value.end}T23:59:59.000`,
      });
    }
  };
  const [currentPage, setCurrentPage] = useState(1);

  const isPreset = timeRange?.includes?.("d") || timeRange?.includes?.("h");
  const [versionValues, setVersionValues] = useState({
    data: [],
    loading: true,
  });
  const { showError } = useMessage();
  type cardDataType = {
    label: string;
    value: string;
    loading: boolean;
    children?: { label: string; value: string };
  };
  const cardsInitialState = {
    [PlatformEnum.ANDROID]: { label: "", value: "", loading: false },
    [PlatformEnum.IOS]: { label: "", value: "", loading: false },
    release: { label: "", value: "", loading: false },
  };
  const [totalCountPage, setTotalCountPage] = useState(1);
  const [issues, setIssues] = useState<{
    data: any[];
    statsData: any;
    cards: {
      release: cardDataType;
      [PlatformEnum.ANDROID]: cardDataType;
      [PlatformEnum.IOS]: cardDataType;
    };
    loading: { stats: boolean; data: boolean };
  }>({
    data: [],
    statsData: {},
    cards: cardsInitialState,
    loading: { stats: true, data: true },
  });

  const onIssueClick = async (id: string) => {
    try {
      const platformQuery = getPlatformQuery();

      // Add filters
      const finalQuery = [platformQuery, query].filter(Boolean).join(" ");

      setIssues((prevState) => ({
        ...prevState,
        loading: { ...prevState.loading, data: true },
      }));
      // Prepare API request parameters
      const params = {
        api_key: loginUser.api_key,
        app_id: appId,
        api_method: ApmApiMethods.ISSUE_EVENTS, //get al events
        ...getApmTimeRange(timeRange, isPreset),
        ...(finalQuery ? { query: finalQuery } : {}), // filter if platformfilter is applied
        collapse: "fullRelease",
        issueId: id,
      };
      // Make API call to fetch event data
      const response = await centralApi(
        "POST",
        API_ENDPOINTS.APM_METRICS_URL,
        params
      );
      const eventIDs = Array.isArray(response)
        ? response.map((item) => ({
            id: item.id,
            dateCreated: item.dateCreated,
          }))
        : [];

      navigate(ROUTES.DASHBOARD_ANALYZE_CRASHES_DETAILS, {
        state: {
          ids: eventIDs, // pass id and time
        },
      });
    } catch {
      setIssues((prevState) => ({
        ...prevState,
        loading: { ...prevState.loading, data: false },
      }));
      showError(t("somethingWrongLabel"));
    }
  };

  /**
   * Fetches session-related metrics and updates the state with the formatted result.
   */
  const getReleaseData = async () => {
    try {
      setIssues((prevState) => ({
        ...prevState,
        cards: {
          ...prevState.cards,
          release: { ...prevState.cards.release, loading: true },
        },
      }));
      // Platform filter
      const platformQuery = getPlatformQuery();

      // Combine with other filters
      const finalQuery = [platformQuery, query].filter(Boolean).join(" ");

      // Map over each field and create an API call for it
      const params = {
        api_key: loginUser.api_key,
        app_id: appId,
        ...(finalQuery ? { query: finalQuery } : {}), // adds `query` only if non-empty

        ...getApmTimeRange(timeRange, isPreset),

        interval: getApmInterval(timeRange, ApmApiMethods.SESSIONS),

        api_method: ApmApiMethods.RELEASES_STATS,
      };
      // Make POST request to fetch session data
      const sessions = await centralApi(
        "POST",
        API_ENDPOINTS.APM_METRICS_URL,
        params
      );
      // Extract releases and get latest release version details
      const releases = sessions || [];
      const latestRelease = !query ? releases?.[0] : "";
      const latestVersion = latestRelease?.version;

      // Format latest version label from version string
      const latestVersionLabel = latestVersion
        ? `${latestVersion.split("@")[1]?.split("+")[0]}(${
            latestVersion.split("+")[1]
          })`
        : undefined;
      // Return formatted data
      const data: cardDataType = {
        label: t("numberOfReleasesLabel"),
        value: `${releases.length}`,
        loading: false,
        children: latestRelease
          ? {
              label: t("latestLabel"),
              value: latestVersionLabel || "",
            }
          : undefined,
      };
      setIssues((prevState) => ({
        ...prevState,
        cards: { ...prevState.cards, release: data },
      }));
    } catch (error) {
      // Handle unexpected errors and ensure loading is turned off
      setIssues((prevState) => ({
        ...prevState,
        cards: { ...prevState.cards, release: cardsInitialState.release },
      }));
    }
  };

  /**
   * Fetches event data from the APM metrics API and updates the state accordingly.
   */
  const getIssues = async (cursor: string | null = null) => {
    try {
      // Set the 'data' loading flag to true before the API call
      setIssues((prevState) => ({
        ...prevState,
        loading: { ...prevState.loading, data: true },
      }));
      const platformQuery = getPlatformQuery();

      // Combine with other filters
      const finalQuery = [platformQuery, query].filter(Boolean).join(" ");

      // Prepare API request parameters
      const params = {
        api_key: loginUser.api_key,
        app_id: appId,
        ...getApmTimeRange(timeRange, isPreset),
        api_method: ApmApiMethods.ISSUES,
        ...(cursor ? { cursor } : {}), // pagination
        limit: 25,
        ...(finalQuery ? { query: finalQuery } : {}), // adds `query` only if non-empty
      };

      // Make API call to fetch event data
      const { data, headers } = await centralApi(
        "POST",
        API_ENDPOINTS.APM_METRICS_URL,
        params
      );
      setTotalCountPage(headers["x-hits"]); //total page limit
      // Update state with the fetched data and set loading to false
      setIssues((prevState) => ({
        ...prevState,
        data: Array.isArray(data) ? data : [],
        loading: { ...prevState.loading, data: false },
      }));
    } catch {
      // On error, reset data to an empty array and turn off loading
      setIssues((prevState) => ({
        ...prevState,
        data: [],
        loading: { ...prevState.loading, data: false },
      }));
    }
  };

  //get all available versions
  const getVersionValues = async () => {
    try {
      // Set the 'data' loading flag to true before the API call
      setVersionValues((prevState) => ({
        ...prevState,
        loading: true,
      }));

      // Prepare API request parameters
      const params = {
        api_key: loginUser.api_key,
        app_id: appId,
    ...getApmTimeRange(timeRange, isPreset),
       api_method: ApmApiMethods.RELEASE_VERSION_VALUES,
      };

      // Make API call to fetch event data
      const data = await centralApi(
        "POST",
        API_ENDPOINTS.APM_METRICS_URL,
        params
      );

      // Update state with the fetched data and set loading to false
      setVersionValues((prevState: any) => ({
        ...prevState,
        data: Array.isArray(data)
          ? data.map(({ key, value }) => ({
              label: value,
              value: key + ":" + value,
            }))
          : [],
        loading: false,
      }));
    } catch {
      // On error, reset data to an empty array and turn off loading
      setVersionValues((prevState) => ({
        ...prevState,
        data: [],
        loading: false,
      }));
    }
  };
  const onPageChange = (page: any) => {
    let cursor = null;

    // Determine direction based on page number and last loaded data
    if (page > currentPage && issues.data.length > 0) {
      // Going forward → use last item
      const last = issues.data[issues.data.length - 1];
      cursor = `${dayjs(last.lastSeen).valueOf()}:0:0`;
    } else if (page < currentPage && issues.data.length > 0) {
      // Going backward → use first item
      const first = issues.data[0];
      cursor = `${dayjs(first.lastSeen).valueOf()}:0:1`;
    }

    // Save current page for next comparison
    setCurrentPage(page);

    // Fetch new data
    getIssues(cursor);
  };

  /**
   * Fetches event statistics and metadata in parallel, then updates the state.
   */
  const getEventStats = async () => {
    try {
      // Set the 'stats' loading flag to true before the API call
      setIssues((prevState) => ({
        ...prevState,
        cards: {
          ...prevState.cards,
          [PlatformEnum.ANDROID]: {
            ...prevState.cards[PlatformEnum.ANDROID],
            loading: true,
          },
          [PlatformEnum.IOS]: {
            ...prevState.cards[PlatformEnum.IOS],
            loading: true,
          },
        },
        loading: { ...prevState.loading, stats: true, cards: true },
      }));

      const fields = [
        {
          method: ApmApiMethods.EVENT_STATS,
          key: ApmApiMethods.EVENT_STATS,
          query: "",
        },
        {
          method: ApmApiMethods.EVENTS_META,
          key: ApmApiMethods.EVENTS_META,
          query: "",
        },
        {
          method: ApmApiMethods.EVENTS_META,
          key: PlatformEnum.ANDROID,
          query: "os.name:Android",
        },
        {
          method: ApmApiMethods.EVENTS_META,
          key: PlatformEnum.IOS,
          query: "os.name:iOS",
        },
      ];

      // Common API parameters for both methods
      const params = {
        api_key: loginUser.api_key,
        app_id: appId,
        ...getApmTimeRange(timeRange, isPreset),
        interval: getApmInterval(timeRange, ApmApiMethods.EVENT_STATS),
        dataset: "errors",
        yAxis: ApmApiFields.COUNT,
        field: ApmApiFields.COUNT,
        query: `!event.type:transaction ${query || ""}`,
        referrer: "api.organization-event-stats",
        partial: 1,
      };

      // Make parallel API calls for EVENT_STATS and EVENTS_META
      const data = Object.fromEntries(
        await Promise.all(
          fields.map(async ({ method, query, key }) => {
            const result = await centralApi(
              "POST",
              API_ENDPOINTS.APM_METRICS_URL,
              {
                ...params,
                api_method: method,
                query: params.query + " " + query,
              }
            );
            return [key, result];
          })
        )
      );

      // Update the state with received data and stop loading
      setIssues((prevState) => ({
        ...prevState,
        statsData: {
          data: data[ApmApiMethods.EVENT_STATS]?.data,
          count: data[ApmApiMethods.EVENTS_META]?.count,
        },
        cards: {
          ...prevState.cards,
          [PlatformEnum.ANDROID]: {
            label: t("androidCrashCountLabel"),
            value: data[PlatformEnum.ANDROID]?.count,
            loading: false,
          },
          [PlatformEnum.IOS]: {
            label: t("iosCrashCountLabel"),
            value: data[PlatformEnum.IOS]?.count,
            loading: false,
          },
        },
        loading: { ...prevState.loading, stats: false },
      }));
    } catch {
      // On error, clear statsData and stop loading
      setIssues((prevState) => ({
        ...prevState,
        statsData: {},
        cards: cardsInitialState,
        loading: { ...prevState.loading, stats: false },
      }));
    }
  };
  //format label for chart
  function getLabelFormat(timeRange: any) {
    const hasRange = timeRange?.start && timeRange?.end;
    const isDayRange =
      (typeof timeRange === "string" && timeRange.includes("d")) || hasRange;

    return isDayRange ? "{value:%b %e %I:%M %p}" : "{value:%I:%M %p}";
  }

  useEffect(() => {
    if (!location.search) {
      getReleaseData();
      getIssues();
      getEventStats();
      getVersionValues();
    }
  }, [location, timeRange, query, platformFilter]);
  return !location.search ? (
    <PageLayout
      title={`${t("analyzeLabel")}`}
      className="crash-analytics-main-dashboard-container"
      topLeftItem={
        <CustomBreadCrumb
          items={[
            {
              title: <span>{t("issuesLabel")}</span>,
            },
          ]}
        />
      }
      topRightItem={
        <div className="d-flex gap-2 justify-content-end">
          <div>
            <span className="me-2">{t("appVersion")}</span>
            <Selector
              className="crash-version-selector"
              allowClear
              value={query}
              loading={versionValues.loading}
              onClear={() => {
                setQuery("");
                dispatch(setCrashAnalytics({ appVersion: "" }));
              }}
              onChange={(value) => {
                setQuery(value);
                dispatch(setCrashAnalytics({ appVersion: value }));
              }}
              options={versionValues.data}
            />
          </div>
          <div>
            <span className="me-2">{t("timeRangeLabel")}</span>

            <DateRangeApmSelector
              defaultValue={timeRange}
              onChange={handleRangeChange}
            />
          </div>
          <div>
            <span className="me-2">{t("platformLabel")}</span>
            <Selector
              className="crash-platform-selector"
              value={platformFilter}
              onChange={(value) => setPlatformFilter(value)}
              options={[
                { label: t("allLabel"), value: PlatformEnum.ALL },
                { label: t("androidLabel"), value: PlatformEnum.ANDROID },
                { label: t("iosLabel"), value: PlatformEnum.IOS },
              ]}
            />
          </div>
        </div>
      }
    >
      <div className="d-flex w-100 gap-3 pb-3 ">
        {Object.values(issues.cards)?.map(
          ({ label, value, children, loading }: cardDataType) => {
            return label ? (
              <CrashSessionCard
                title={label}
                percentage={value}
                metaData={children}
                loading={loading}
              />
            ) : (
              <></>
            );
          }
        )}
      </div>

      <div className="crash-analytics-session-chart-container mb-3">
        <div className="d-flex justify-content-between">
          <strong>{t("numberOfErrorsLabel")}</strong>
          {issues.statsData.loading ? (
            <SkeletonLoader type="button" />
          ) : (
            <strong>
              {t("totalLabel")}: {issues?.statsData?.count}
            </strong>
          )}
        </div>
        <div className="mt-3">
          <CustomHighChart
            options={{
              loading: issues.loading.stats,
              chart: {
                type: "column",
                height: 300,
              },
              title: {
                text: undefined,
              },
              xAxis: {
                type: "datetime",
                tickLength: 0,
                title: {
                  text: undefined,
                },
                labels: {
                  format: getLabelFormat(timeRange),
                },
              },
              tooltip: {
                shared: true,
                crosshairs: true,
                xDateFormat: "%b %e,%Y %I:%M %p",
              },
              legend: {
                enabled: false,
              },
              navigation: {
                buttonOptions: {
                  enabled: false,
                },
              },
              time: {
                timezoneOffset: -330, // minus because it's ahead of UTC
              },
              yAxis: {
                min: 0,
                title: {
                  text: undefined,
                },
              },
              series: [
                {
                  name: t("thisPeriodLabel"),
                  data: issues?.statsData?.data?.map(
                    ([timestamp, data]: [number, { count: number }[]]) => [
                      Number(timestamp) * 1000,
                      data[0]?.count ?? 0,
                    ]
                  ),
                },
              ],
            }}
          />
        </div>
      </div>
      <List
        itemLayout="vertical"
        loading={issues.loading.data}
        dataSource={[...(issues.data || [])].sort((a, b) => {
          const aTime = a.lastSeen ? dayjs(a.lastSeen).valueOf() : 0;
          const bTime = b.lastSeen ? dayjs(b.lastSeen).valueOf() : 0;
          return bTime - aTime;
        })}
        pagination={{
          current: currentPage,
          pageSize: 25,
          total: totalCountPage, //  (total items)
          showSizeChanger: false,
          simple: true,
          showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
          onChange: (page) => onPageChange(page),
        }}
        renderItem={(item) => (
          <div
            className="crash-card-main-container mb-2 rounded p-3"
            key={item?.id}
            onClick={() => {
              onIssueClick(item?.id);
            }}
          >
            <div className="d-flex justify-content-between">
              <div>
                <div className="d-flex gap-2 mb-2">
                  {<Tag color="red">{item.metadata?.type || item.title}</Tag>}
                  <span className="text-muted">{item?.culprit}</span>
                </div>

                <div className="mb-2 d-flex gap-2 align-items-center">
                  <span className="crash-card-error-dot"></span>
                  <strong>{item.metadata?.value}</strong>
                </div>

                <div className="d-flex flex-wrap gap-2 mt-2">
                  <div className="d-flex align-items-center gap-1">
                    <ClockCircleOutlined />{" "}
                    <span>{dayjs(item?.lastSeen).fromNow()}</span>
                  </div>
                </div>
              </div>
              <div>
                <strong>
                  {t("eventsLabel")} : {item?.filtered?.count || item.count}
                </strong>
              </div>
            </div>
          </div>
        )}
      />
    </PageLayout>
  ) : (
    <CrashDetailedView />
  );
};
export default Crashes;
